// the Angular framework uses this test for whether an input is numeric
const isNumeric = (value: any): boolean => !isNaN(value - parseFloat(value));

/**
 * Makes a best effort attempt to convert a string to a number. This method goes deeper than simply
 * calling parseInt/parseFloat and will attempt to handle common human readable numbers "correctly".
 * @param value string The string value.
 * @return number Returns the number (or NaN if the method fails entirely).
 * TODO : Handle accounting negatives? e.g. ($50.00)
 */
export const toNumber = (value: string): number => {
  // trim and remove leading $ and trailing %
  const squeezed = value
    .trim()
    .replace(/^\$/g, "")
    .replace(/%$/g, "");
  // empty strings are not numbers
  if (value.length < 1) {
    return NaN;
  }
  // if we are numeric, return the parsed number immediately
  if (isNumeric(squeezed)) {
    return parseFloat(squeezed);
  }
  let cleanValue = squeezed;
  let negativeModifier = 1;
  // check for a leading hyphen and if so, strip it (setting a flag to -1)
  if (cleanValue.charAt(0) === "-") {
    negativeModifier = -1;
    // handle a single space between the hyphen and the number
    if (value.charAt(1) === " ") {
      cleanValue = value.substr(2);
    } else {
      cleanValue = value.substr(1);
    }
    // if we are fully numeric after the strip of the leading hyphen
    // then return the parsed number
    if (isNumeric(cleanValue)) {
      return -1 * parseFloat(cleanValue);
    }
  }
  // check if we have the special case of the comma being used as a decimal point
  const commaSplit = cleanValue.split(",");
  // if there are:
  // - no periods
  // - exactly one comma and
  // - more than or less than three characters trailing it, assume the comma means a decimal separator
  if (cleanValue.indexOf(".") === -1 && commaSplit.length === 2 && commaSplit[1].length !== 3) {
    cleanValue = commaSplit.join(".");
    if (isNumeric(cleanValue)) {
      return negativeModifier * parseFloat(cleanValue);
    }
  } else {
    // replace commas with spaces as a separator
    cleanValue = cleanValue.replace(/,/g, " ");
  }
  // divide the number based on before and after the decimal
  let wholePart = cleanValue;
  let decimalPart = "0";
  const decimalSplit = cleanValue.split(".");
  if (decimalSplit.length > 2) {
    // cannot have more than 1 decimal
    return NaN;
  } else if (decimalSplit.length === 2) {
    wholePart = decimalSplit[0];
    decimalPart = decimalSplit[1];
  }
  // the decimal part must be a normal numeral now
  if (!isNumeric(decimalPart)) {
    return NaN;
  }
  // split the number based on spaces
  const spaceSplit = wholePart.split(" ");
  // find an invalid "group" of numbers
  const invalidGroup = spaceSplit.find((group, index) => {
    // empty groups (i.e. multiple spaces in succession) are always invalid
    if (group.length === 0) {
      return true;
    }
    // groups should be length 3
    if (group.length !== 3) {
      // unless they are the first group
      if (index > 0) {
        return true;
      }
    }
    return !isNumeric(group);
  });
  if (!!invalidGroup) {
    return NaN;
  }
  wholePart = spaceSplit.join("");
  if (isNumeric(wholePart)) {
    return negativeModifier * (parseFloat(wholePart) + parseFloat(`0.${decimalPart}`));
  }
  return NaN;
};
